import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ExternalLink, X } from 'lucide-react'

import { MarketEventCard, dateLongueFr, dateAffichee, heureFr, ageFr, type MarketEventLike } from './MarketEventCard.js'

/**
 * DÉTAIL D'UNE SOIRÉE CONCURRENTE — SURFACE UNIQUE.
 *
 * Cliquer une carte ouvrait Instagram. On quittait Gestion pour lire une
 * information que Gestion possède déjà, mieux structurée — et on perdait au
 * passage l'affiche analysée, l'impact et la fiabilité.
 *
 * Désormais : le clic ouvre CE panneau, et « Voir sur Instagram » devient une
 * action secondaire, assumée comme sortie externe.
 *
 * Une seule implémentation, utilisée par toutes les surfaces — Autour de
 * Rennes, fiche établissement, contexte d'une soirée Live.
 */

export interface MarketEventDetail extends MarketEventLike {
  business_night_date?: string | null
  announced_date?: string | null
  announced_time?: string | null
  category?: string | null
  relevance_factors?: { label: string; points: number }[]
}

const FIABILITE_FR: Record<string, string> = {
  CONFIRMED: 'Source sûre', LIKELY: 'Source fiable', UNCERTAIN: 'À confirmer',
}
const IMPACT_FR: Record<string, string> = {
  FORT: 'Impact fort', MOYEN: 'Impact moyen', FAIBLE: 'Impact faible',
}

/** Champs dont la valeur mérite toute la largeur du panneau. */
const LARGES = new Set(['Adresse', 'Artistes', 'Sources'])

/** « ven. 28 août » — la nuit telle qu'on la nomme. */
function nuitFr(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(`${iso}T12:00:00Z`)
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris',
  })
}

export function MarketEventDrawer({ event, onClose }: {
  event: MarketEventDetail | null
  onClose: () => void
}) {
  const [zoom, setZoom] = useState(false)

  useEffect(() => {
    if (!event) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Échap referme d'abord le zoom, puis le panneau : sinon on perd les
      // deux d'un coup et l'utilisateur repart de la liste.
      if (zoom) setZoom(false)
      else onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [event, zoom, onClose])

  useEffect(() => { setZoom(false) }, [event?.id])

  if (!event) return null
  const e = event
  const capture = e.capture_content_id
    ? `/api/v1/market/social/stories/${e.capture_content_id}/capture` : null
  const instagram = e.sources.find((s) => /instagram/i.test(s.name))
  const handle = /@([\w.]+)/.exec(instagram?.name ?? '')?.[0] ?? null
  const lienInstagram = instagram?.url
    ?? (handle ? `https://www.instagram.com/${handle.slice(1)}/` : null)

  const infos: [string, string | null][] = [
    ['Nuit', nuitFr(e.business_night_date)],
    // L'instant civil réel est dit ici, à côté de la nuit : c'est le seul
    // endroit où la nuance a sa place.
    ['Ouverture réelle', e.time_known !== false
      ? `${dateLongueFr(e.starts_at)} · ${heureFr(e.starts_at)}` : null],
    ['Fin annoncée', e.ends_at && e.time_known !== false ? heureFr(e.ends_at) : null],
    // §19 — ce qui était ÉCRIT, à côté de ce qu'on en a déduit.
    ['Annoncé sur l’affiche', e.announced_date
      ? [e.announced_date, e.announced_time].filter(Boolean).join(' · ') : null],
    ['Artistes', (e.artists?.length ?? 0) > 0 ? e.artists!.join(' · ') : null],
    ['Établissement', e.venue_name],
    ['Ville', e.city ?? null],
    ['Adresse', e.address ?? null],
    ['Distance', e.distance_km != null ? `${e.distance_km.toLocaleString('fr-FR')} km` : null],
    ['Style', e.category ?? null],
    ['Fiabilité', FIABILITE_FR[e.confidence] ?? e.confidence],
    ['Impact', IMPACT_FR[e.relevance_level] ?? e.relevance_level],
    ['Détectée', ageFr(e.first_seen_at ?? e.last_verified_at)],
    ['Sources', e.sources.length > 1 ? `${e.sources.length} sources concordantes` : null],
  ]

  /*
   * PORTALISÉ, ET PAS PAR CONFORT.
   *
   * `.lc-route` porte un `transform` — l'identité, pour la transition de page.
   * Il suffit : n'importe quel transform crée un bloc conteneur, et un
   * `position: fixed` cesse alors de se référer au viewport. Le panneau se
   * calait sur la hauteur de la PAGE : 4 317 px sur un écran de 477, avec
   * seul son pied visible, et une largeur héritée du contenu.
   *
   * Rendu dans `body`, il retrouve le viewport comme référence. Même remède
   * que pour les autres surfaces flottantes du projet.
   */
  return createPortal(
    <>
      <div className="med-overlay" role="presentation" onClick={onClose} />
      <aside className="med" role="dialog" aria-modal="true" aria-label={e.title}>
        <header className="med__head">
          <div className="med__head__id">
            <p className="med__date">{dateAffichee(e)}</p>
            <h2 className="med__title">{e.title}</h2>
            <p className="med__venue">{[e.venue_name, e.city].filter(Boolean).join(' · ')}</p>
          </div>
          <button type="button" className="lc-iconbtn" onClick={onClose} aria-label="Fermer">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="med__body">
          <div className="med__badges">
            <span className={`mev__badge mev__badge--fia-${
              e.confidence === 'CONFIRMED' ? 'sure' : e.confidence === 'UNCERTAIN' ? 'doute' : 'ok'}`}>
              {FIABILITE_FR[e.confidence] ?? e.confidence}
            </span>
            <span className={`mev__badge mev__badge--imp-${e.relevance_level.toLowerCase()}`}>
              {IMPACT_FR[e.relevance_level] ?? e.relevance_level}
            </span>
          </div>

          {/* §22 — LA PREUVE EN GRAND. Une miniature ne se lit pas : c'est
              l'affiche entière qui porte l'information, et c'est elle qui
              justifie la fiabilité affichée. */}
          {capture ? (
            <button type="button" className="med__proof" onClick={() => setZoom(true)}
              aria-label="Agrandir l’affiche analysée">
              <img src={capture} alt={`Affiche de « ${e.title} »`} />
            </button>
          ) : null}

          <dl className="med__kv">
            {infos.filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
              // Adresse et artistes débordent d'une demi-colonne : on leur
              // rend la largeur entière plutôt que de les couper.
              <div key={k} className={LARGES.has(k) ? 'med__kv--large' : undefined}>
                <dt>{k}</dt><dd>{v}</dd>
              </div>
            ))}
          </dl>

          {(e.relevance_factors?.length ?? 0) > 0 ? (
            <section className="med__why">
              <h3 className="med__why__title">Pourquoi cet impact</h3>
              <ul>
                {e.relevance_factors!.map((f, i) => (
                  <li key={i}><span>{f.label}</span><em>+{f.points}</em></li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="med__prov">
            <h3 className="med__why__title">Provenance</h3>
            {e.sources.map((s, i) => (
              <p key={i} className="lc-hint med__prov__line">
                {s.name}{s.seen_at ? ` · vue ${ageFr(s.seen_at)}` : ''}
              </p>
            ))}
          </section>
        </div>

        <footer className="med__foot">
          {/* §99 — sortie externe ASSUMÉE, et secondaire : l'information
              principale est ici, pas sur Instagram. */}
          {lienInstagram ? (
            <a className="lc-btn" href={lienInstagram} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} aria-hidden="true" /> Voir sur Instagram
            </a>
          ) : null}
          <button type="button" className="lc-btn lc-btn--primary" onClick={onClose}>Fermer</button>
        </footer>
      </aside>

      {zoom && capture ? (
        <div className="med__zoom" role="dialog" aria-label="Affiche en grand"
          onClick={() => setZoom(false)}>
          <img src={capture} alt={`Affiche de « ${e.title} »`} />
        </div>
      ) : null}
    </>,
    document.body,
  )
}

/** Réexport pratique : les surfaces n'importent qu'un fichier. */
export { MarketEventCard }
